// Device detection and app installation check helpers

import { getWebAppType } from 'shared/helpers/deviceType/webAppType';
import { WEB_APP_TYPE } from 'shared/constants';
import {
  APP_INSTALL_CACHE_CONFIG,
  ANDROID_APP_PACKAGES,
  BANNER_DISMISS_CONFIG,
} from 'shared/components/AppMigration/constants';

/**
 * Detects if the current device is Android (mobile web, not in-app)
 */
export const isAndroidDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const webAppType = getWebAppType();
  if (webAppType === WEB_APP_TYPE.MWEB) {
    const userAgent = navigator.userAgent || '';
    return /android/i.test(userAgent);
  }
  return false;
};

/**
 * Detects if the current device is iOS (mobile web, not in-app)
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const webAppType = getWebAppType();
  if (webAppType === WEB_APP_TYPE.MWEB) {
    const userAgent = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(userAgent);
  }
  return false;
};

/**
 * Detects if user is on mobile web (not in native app)
 */
export const isMobileWeb = (): boolean => {
  if (typeof window === 'undefined') return false;

  const webAppType = getWebAppType();
  // Only show on MWEB, not when user is already in native app (ANDROID/IOS)
  return webAppType === WEB_APP_TYPE.MWEB;
};

/**
 * Returns the platform type
 */
export const getPlatformType = (): 'android' | 'ios' | 'unknown' => {
  if (isAndroidDevice()) return 'android';
  if (isIOSDevice()) return 'ios';
  return 'unknown';
};

/**
 * Get cached app installation status from localStorage
 */
const getCachedAppInstallStatus = (): boolean | null => {
  if (typeof window === 'undefined') return null;

  try {
    const cached = localStorage.getItem(APP_INSTALL_CACHE_CONFIG.storageKey);
    if (cached) {
      const { isInstalled, timestamp } = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - timestamp < APP_INSTALL_CACHE_CONFIG.cacheDurationMs) {
        return isInstalled;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
};

/**
 * Cache the app installation status in localStorage
 */
const setCachedAppInstallStatus = (isInstalled: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(
      APP_INSTALL_CACHE_CONFIG.storageKey,
      JSON.stringify({
        isInstalled,
        timestamp: Date.now(),
      })
    );
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Clear cached app install status (useful for testing/debugging)
 */
export const clearAppInstallCache = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(APP_INSTALL_CACHE_CONFIG.storageKey);
  } catch {
    // Ignore errors
  }
};

/**
 * Checks if the native app is installed on Android devices.
 * Uses the getInstalledRelatedApps API (available on Android Chrome 80+)
 *
 * Requirements for this API to work:
 * 1. Website must be served over HTTPS
 * 2. manifest.json must have related_applications configured
 * 3. Android app's assetlinks.json must list the website domain
 *
 * For iOS: Always returns false (no reliable detection method)
 * iOS will always show "Use App" and AppsFlyer handles the smart redirect
 *
 * @returns Promise<boolean> - true if app is detected on Android, false otherwise
 */
export const checkIfAppInstalled = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;

  // Check cache first
  const cachedStatus = getCachedAppInstallStatus();
  if (cachedStatus !== null) {
    return cachedStatus;
  }

  // Only attempt detection on Android
  if (!isAndroidDevice()) {
    return false;
  }

  try {
    // Use getInstalledRelatedApps API if available (Android Chrome 80+)
    if ('getInstalledRelatedApps' in navigator) {
      const relatedApps = await (
        navigator as unknown as {
          getInstalledRelatedApps: () => Promise<
            Array<{ id?: string; url?: string; platform?: string }>
          >;
        }
      ).getInstalledRelatedApps();

      // Check if our app is in the list
      const isInstalled =
        relatedApps &&
        relatedApps.length > 0 &&
        relatedApps.some((app) => {
          // Match by package ID
          if (app.id && ANDROID_APP_PACKAGES.includes(app.id)) return true;
          // Match by URL containing package name
          if (
            app.url &&
            ANDROID_APP_PACKAGES.some((pkg) => app.url?.includes(pkg))
          )
            return true;
          // If platform is 'play' and we got a match from manifest, it's our app
          if (app.platform === 'play') return true;
          return false;
        });

      // Cache the result
      setCachedAppInstallStatus(isInstalled);
      return isInstalled;
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Check if the banner was dismissed recently
 */
export const isBannerDismissed = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const dismissed = localStorage.getItem(BANNER_DISMISS_CONFIG.storageKey);
    if (!dismissed) return false;

    const dismissedTime = parseInt(dismissed, 10);
    const now = Date.now();

    return now - dismissedTime < BANNER_DISMISS_CONFIG.dismissDurationMs;
  } catch {
    return false;
  }
};

/**
 * Mark the banner as dismissed
 */
export const dismissBanner = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(
      BANNER_DISMISS_CONFIG.storageKey,
      Date.now().toString()
    );
  } catch {
    // Silently fail if localStorage is not available
  }
};
